import React from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Control, Controller } from "react-hook-form";
import { AiOutlineCalendar } from "react-icons/ai";
import ErrorsMessage from "app/components/common/errors/ErrorsMessage";
import moment from "moment";

interface DatePickerProps {
  name: string;
  control: Control<any>;
  label?: string;
  placeholder?: string;
  rules?: Record<string, any>;
  className?: string;
  validateAge?: boolean;
  validateFutureDate?: boolean;
  isPastDate?: boolean;
  minDate?: Date;
  maxDate?: Date;
  disabled?: boolean;
  required?: boolean;
}

const CustomDatePicker: React.FC<DatePickerProps> = ({
  name,
  control,
  label,
  placeholder,
  rules = {},
  className,
  validateAge = false,
  validateFutureDate = false,
  isPastDate = false,
  minDate,
  maxDate,
  disabled = false,
  required = false,
}) => {
  const isRequired = !!(required || rules?.required);

  const validateAgeFn = (value: Date) => {
    if (!value) return true; // Skip validation if no value
    const selectedDate = new Date(value);
    const currentDate = new Date();
    const age = currentDate.getFullYear() - selectedDate.getFullYear();
    const monthDiff = currentDate.getMonth() - selectedDate.getMonth();

    // Check if the user is 18 or older
    if (age < 18 || (age === 18 && monthDiff < 0)) {
      return "You must be 18 or older.";
    }
    return true; // Valid age
  };

  const validateFutureDateFn = (value: Date) => {
    if (!value) return true; // Skip validation if no value
    const selectedDate = new Date(value);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    selectedDate.setHours(0, 0, 0, 0);

    if (selectedDate <= today) {
      return "Date must be in the future.";
    }
    return true;
  };

  const validatePastDateFn = (value: Date) => {
    if (!value) return true; // Skip validation if no value
    const selectedDate = new Date(value);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    selectedDate.setHours(0, 0, 0, 0);

    if (selectedDate >= today) {
      return "Date must be in the past.";
    }
    return true;
  };

  const parseDate = (value: any): Date | null => {
    if (!value) return null;

    // If it's already a Date object
    if (value instanceof Date) return value;

    // If it's a string in YYYY-MM-DD format
    if (
      typeof value === "string" &&
      moment(value, "YYYY-MM-DD", true).isValid()
    ) {
      return moment(value, "YYYY-MM-DD").toDate();
    }

    // If it's a string in DD/MM/YYYY format
    if (
      typeof value === "string" &&
      moment(value, "DD/MM/YYYY", true).isValid()
    ) {
      return moment(value, "DD/MM/YYYY").toDate();
    }

    return null;
  };

  const composedRules = !disabled
    ? {
        ...(required ? { required: "Date is required" } : {}),
        ...rules,
        ...(validateAge || validateFutureDate || isPastDate
          ? {
              validate: (value: Date) => {
                if (!value) return true;

                if (validateAge) {
                  const result = validateAgeFn(value);
                  if (result !== true) return result;
                }

                if (validateFutureDate) {
                  const result = validateFutureDateFn(value);
                  if (result !== true) return result;
                }

                if (isPastDate) {
                  const result = validatePastDateFn(value);
                  if (result !== true) return result;
                }

                return true;
              },
            }
          : {}),
      }
    : {};
  return (
    <div className={`date-picker-wrapper relative text-left ${className}`}>
      {label && (
        <label
          htmlFor={name}
          className="block font-Mulish text-base font-normal text-lightBlue mb-1"
        >
          {label.charAt(0).toUpperCase() + label.slice(1).toLowerCase()}{" "}
          {isRequired && <span className="text-red-500">*</span>}
        </label>
      )}
      <div className="date-picker-container relative">
        <Controller
          // key={isRequired ? "not-required" : "required"} // Forces re-render
          key={isRequired ? "no-rule" : "with-rule"}
          name={name}
          control={control}
          rules={composedRules}
          render={({ field: { onChange, value }, fieldState: { error } }) => {
            return (
              <>
                <DatePicker
                  id={name}
                  // selected={value ? moment(value, "YYYY-MM-DD").toDate() : null}
                  selected={parseDate(value)}
                  onChange={(date) =>
                    onChange(date ? moment(date).format("YYYY-MM-DD") : null)
                  }
                  autoComplete="off"
                  showMonthDropdown
                  showYearDropdown
                  scrollableYearDropdown
                  yearDropdownItemNumber={100}
                  minDate={minDate ? minDate : undefined}
                  maxDate={maxDate ? maxDate : undefined} // Maximum year (current year)
                  placeholderText={placeholder}
                  dateFormat="dd/MM/yyyy"
                  className={`custom_datepicker w-full px-4 py-4 border-2 font-Mulish rounded-[10px] focus:outline-none placeholder-gray-400  ${
                    error ? "border-red-500" : "border-[#EAECF0]"
                  }`}
                  disabled={disabled}
                />
                <AiOutlineCalendar
                  className={`absolute ${
                    error ? "top-[50%]" : "top-1/2"
                  }  right-4 transform -translate-y-1/2 calendar-icon text-[#98A2B3] text-[25px] cursor-pointer`}
                  onClick={() => {
                    const input = document.getElementById(name);
                    input?.focus();
                  }}
                />
                {error && (
                  <ErrorsMessage
                    title={error.message}
                    className="text-right absolute top-[100%] right-0"
                  />
                )}
              </>
            );
          }}
        />
      </div>
    </div>
  );
};

export default CustomDatePicker;
